import { useCreateExtensionInstallationScriptMutationMutation } from "@graphql/clients/clients.g";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Callout } from "@design-system/Callout/Callout";
import { Card } from "@design-system/Card/Card";
import { Code } from "@design-system/Code/Code";
import { Text } from "@design-system/Text/Text";

// Module-level cache to prevent duplicate requests across all instances during StrictMode
const scriptCache = new Map<string, Promise<string | null>>();

type Props = {
  clientId: string;
};

export const ExtensionInstructionsCard = ({ clientId }: Props) => {
  const createScriptMutation =
    useCreateExtensionInstallationScriptMutationMutation();

  const [configJson, setConfigJson] = useState<string | null>(null);
  const [configData, setConfigData] = useState<Record<string, string> | null>(
    null,
  );

  useEffect(() => {
    if (!clientId) return;

    const fetchScript = async () => {
      const cacheKey = `script-${clientId}`;

      // Check if we already have a request in flight for this clientId
      if (scriptCache.has(cacheKey)) {
        try {
          const cachedJsonStr = await scriptCache.get(cacheKey);
          if (cachedJsonStr) {
            setConfigJson(cachedJsonStr);
            setConfigData(JSON.parse(cachedJsonStr));
          }
        } catch (e) {
          console.error("Failed to parse cached config json", e);
        }
        return;
      }

      // Execute request directly instead of caching the promise object itself
      // This is simpler and avoids resolving an old Promise reference across unmount boundaries
      try {
        const response = await createScriptMutation.mutateAsync({
          input: {
            clientId: clientId,
          },
        });

        const jsonStr =
          response.createExtensionInstallationScript?.configJson || null;

        // Save to cache as a resolved string literal promise
        scriptCache.set(cacheKey, Promise.resolve(jsonStr));

        if (jsonStr) {
          setConfigJson(jsonStr);
          setConfigData(JSON.parse(jsonStr));
        }
      } catch (error) {
        console.error("Failed to fetch installation script:", error);
        scriptCache.delete(cacheKey);
      }
    };

    fetchScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDownloadConfig = () => {
    if (!configJson) return;

    const blob = new Blob([configJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `config.json`;
    a.click();
    URL.revokeObjectURL(url);

    toast.success("Configuration downloaded successfully");
  };

  const handleCopyConfig = async () => {
    if (!configJson) return;

    try {
      await navigator.clipboard.writeText(configJson);
      toast.success("Configuration copied to clipboard");
    } catch (error) {
      console.error("Failed to copy configuration:", error);
      toast.error("Failed to copy configuration to clipboard");
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Header.Title>
          Instructions for installing the extension
        </Card.Header.Title>
      </Card.Header>
      <Card.Body noOverflow>
        <Box gap={32} direction="column">
          <Box gap={12} direction="column">
            <Text weight={700} size={16}>
              1. Download the Executables
            </Text>
            <Text spacing="relaxed">
              Download the standalone binaries for installation and
              uninstallation. No runtime dependencies are required.
            </Text>
            <div style={{ marginTop: 4 }}>
              <Box gap={8} direction="row">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    window.open(
                      `${import.meta.env.VITE_EXTENSION_HOST}/affire-installer.exe`,
                      "_blank",
                    );
                  }}
                  icon="faFileExport"
                >
                  Download Installer
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    window.open(
                      `${import.meta.env.VITE_EXTENSION_HOST}/affire-uninstaller.exe`,
                      "_blank",
                    );
                  }}
                  icon="faFileExport"
                >
                  Download Uninstaller
                </Button>
              </Box>
            </div>
          </Box>

          <Box gap={12} direction="column">
            <Text weight={700} size={16}>
              2. Configuration
            </Text>
            <Text spacing="relaxed">
              To install the extension for this specific tenant, you need a
              configuration file. Download the pre-configured config.json for
              this client.
            </Text>

            <Callout type="warning" title="Important">
              This configuration contains secrets and is tailored specifically
              for this client. Do not use it for other clients.
            </Callout>

            <div style={{ marginTop: 4 }}>
              <Box gap={8} direction="row">
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleDownloadConfig}
                  disabled={!configJson}
                  icon="faFileExport"
                >
                  Download config.json
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCopyConfig}
                  disabled={!configJson}
                  icon="faFileCode"
                >
                  Copy Config
                </Button>
              </Box>
            </div>
          </Box>

          <Box gap={12} direction="column">
            <Text weight={700} size={16}>
              3. Installation Methods
            </Text>
            <Text spacing="relaxed">
              You must run the installer as an Administrator. There are two ways
              to provide the configuration:
            </Text>

            <div style={{ marginTop: 8 }}>
              <Box gap={24} direction="column">
                <Box gap={8} direction="column">
                  <Text weight={700}>Method A: UI / Double-click</Text>
                  <Text spacing="relaxed">
                    Place the downloaded config.json file in the same directory
                    as affire-installer.exe. Double-click the installer and run
                    it as an Administrator. It will automatically detect the
                    configuration file.
                  </Text>
                </Box>

                <Box gap={8} direction="column">
                  <Text weight={700}>Method B: Command Line (CLI)</Text>
                  <Text spacing="relaxed">
                    Run the installer via command line, providing all the
                    configuration arguments directly.
                  </Text>
                  {configData ? (
                    <Code display="block" title="Command Line Installation">
                      {`affire-installer.exe --client-id "${configData.clientId}" --tenant-name "${configData.tenantName}" --api-key "${configData.apiKey}" --token "${configData.token}" --ingest-url "${configData.ingestUrl}"`}
                    </Code>
                  ) : (
                    <Code display="block" title="Command Line Installation">
                      {`Loading configuration...`}
                    </Code>
                  )}
                </Box>
              </Box>
            </div>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
};
